import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AuthShell } from '@/components/templates/AuthShell';
import { Button } from '@/components/atoms/Button';
import { ErrorStateBlock } from '@/components/organisms/ErrorStateBlock';
import { Spinner } from '@/components/atoms/Spinner';
import { apiClient } from '@/lib/http/apiClient';

export const Route = createFileRoute('/invite/accept')({
  component: InviteAcceptPage,
  validateSearch: (search): { token?: string } => ({ token: typeof search.token === 'string' ? search.token : undefined }),
});

function InviteAcceptPage() {
  const { token } = useSearch({ from: '/invite/accept' });
  const { isAuthenticated, isLoading: authLoading, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const accept = useMutation({
    mutationFn: async (t: string) => (await apiClient.post('/api/v1/invites/accept', { token: t })).data,
    onSuccess: () => navigate({ to: '/' }),
  });

  useEffect(() => {
    if (token && isAuthenticated && !accept.isPending && !accept.data && !accept.error) {
      accept.mutate(token);
    }
  }, [token, isAuthenticated, accept]);

  if (!token) return <AuthShell><ErrorStateBlock title="No invite token" detail="The link you followed is missing the token." /></AuthShell>;
  if (authLoading) return <AuthShell><Spinner label="Loading" /></AuthShell>;
  if (!isAuthenticated) {
    return (
      <AuthShell>
        <h1 className="font-display text-title text-foreground">Accept invitation</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">Sign in to accept this invite.</p>
        <Button variant="primary" fullWidth className="mt-6" onClick={() => loginWithRedirect()}>Sign in →</Button>
      </AuthShell>
    );
  }
  if (accept.error) return <AuthShell><ErrorStateBlock title="Invite invalid" detail="This invitation may have expired or been revoked." /></AuthShell>;
  return <AuthShell><Spinner label="Accepting invitation" /></AuthShell>;
}
